import { faChevronRight, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo } from "react";
import styled from "styled-components";

import color from "constants/color";
import { range } from "utils/arrayUtils";

const Wrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 30px;
`;
const PrevNextButton = styled.button<{ disabled: boolean }>`
  width: 80px;
  height: 40px;
  border-radius: 4px;
  position: relative;
  background: ${({ disabled }) => (disabled ? color.text.disabled : color.white)};
  border: 1px solid ${({ disabled }) => (disabled ? color.text.disabled : color.text.gray)};
  color: ${({ disabled }) => (disabled ? color.white : color.text.gray)};
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  &:hover {
    background: ${({ disabled }) => (disabled ? color.text.disabled : color.background)};
  }
`;
const PrevButton = styled(PrevNextButton)`
  padding-left: 16px;
`;
const NextButton = styled(PrevNextButton)`
  padding-right: 16px;
`;
const LeftIcon = styled(FontAwesomeIcon)`
  position: absolute;
  top: 12px;
  left: 16px;
`;
const RightIcon = styled(FontAwesomeIcon)`
  position: absolute;
  top: 12px;
  right: 16px;
`;
const PageLink = styled.div<{ disabled: boolean }>`
  padding: 0 8px;
  color: ${({ disabled }) => (disabled ? color.text.disabled : color.text.gray)};
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
`;

const PaginationForAtPort = ({
  totalPageCount,
  pageOption,
  setPageOption,
  startCursor,
  endCursor,
  hasNextPage,
  hasPreviousPage,
}: {
  totalPageCount: number;
  pageOption: AtPortPageOption;
  setPageOption: React.Dispatch<React.SetStateAction<AtPortPageOption>>;
  startCursor: string;
  endCursor: string;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}) => {
  const displayNumberArray = useMemo(() => {
    if (pageOption.pageIndex <= 0 || totalPageCount <= 1 || pageOption.pageIndex > totalPageCount) return [];

    // 1ページ目の場合は、[1, 2]を返す
    // Nページ目の場合は、[N-1, N, N+1]を返す
    // 最終ページの場合は、[N-1, N]を返す
    return pageOption.pageIndex === 1
      ? [1, 2]
      : 3 >= totalPageCount
      ? [...range(1, totalPageCount)]
      : pageOption.pageIndex === 2
      ? [...range(1, 3)]
      : pageOption.pageIndex === totalPageCount - 1
      ? [...range(totalPageCount - 2, totalPageCount)]
      : pageOption.pageIndex === totalPageCount
      ? [...range(totalPageCount - 1, totalPageCount)]
      : [...range(pageOption.pageIndex - 1, pageOption.pageIndex + 1)];
  }, [pageOption.pageIndex, totalPageCount]);

  // 表示すべきものがない場合は、React.Fragmentを返す
  if (displayNumberArray.length === 0) return <></>;

  const onBackPage = () => {
    setPageOption((prev) => ({
      after: undefined,
      before: startCursor,
      pageIndex: prev.pageIndex - 1,
    }));
  };
  const onNextPage = () => {
    setPageOption((prev) => ({
      after: endCursor,
      before: undefined,
      pageIndex: prev.pageIndex + 1,
    }));
  };
  const onMovePage = (movePage: number, currentPage: number) => {
    if (movePage === currentPage) return;
    setPageOption(() => ({
      after: movePage > currentPage ? endCursor : undefined,
      before: movePage < currentPage ? startCursor : undefined,
      pageIndex: movePage,
    }));
  };

  return (
    <Wrap>
      <PrevButton disabled={!hasPreviousPage} onClick={onBackPage}>
        <LeftIcon icon={faChevronLeft} />
        前へ
      </PrevButton>
      {pageOption.pageIndex > 2 && <div>...</div>}
      {displayNumberArray.map((p, index) => (
        <PageLink
          key={`link_${index}`}
          disabled={pageOption.pageIndex === p}
          onClick={() => onMovePage(p, pageOption.pageIndex)}
        >
          {p}
        </PageLink>
      ))}
      {pageOption.pageIndex <= totalPageCount - 2 && <div>...</div>}
      <NextButton disabled={!hasNextPage} onClick={onNextPage}>
        次へ
        <RightIcon icon={faChevronRight} />
      </NextButton>
    </Wrap>
  );
};

export default PaginationForAtPort;
